import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getCalApi } from "@calcom/embed-react";
import Navigation from '../../components/Navigation/Navigation';
import { ComparisonSection } from '../../../shared/components';
import justTapNewImage from '../../../shared/assets/images/justtapnew.png';
import phoneDemoImage from '../../../shared/assets/images/phonedemo.png';
import flagImage from '../../../shared/assets/images/flag.png';
import './HomePage.css';

const HomePage = () => {
  const [currentWord, setCurrentWord] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [showBallAnimation, setShowBallAnimation] = useState(false);
  const [ballPosition, setBallPosition] = useState({ x: 0, y: 0 });
  const [cardBounces, setCardBounces] = useState({ card1: false, card2: false, card3: false });
  const [currentImageSet, setCurrentImageSet] = useState(0); // 0 for cards, 1 for phone demo
  const [isTransitioning, setIsTransitioning] = useState(false);
  const words = ['QR Codes', 'App to download', 'Stamp cards', 'Forms to fill', 'Customer Sign up'];

  const handleLearnMoreClick = (e) => {
    e.preventDefault();
    const howItWorksSection = document.querySelector('.how-it-works-section');
    if (howItWorksSection) {
      howItWorksSection.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  // Cal.com embed initialization
  useEffect(() => {
    (async function () {
      const cal = await getCalApi({"namespace":"problem-discovery"});
      cal("ui", {"theme":"light","cssVarsPerTheme":{"light":{"cal-brand":"#FFA366"},"dark":{"cal-brand":"#FFA366"}},"hideEventTypeDetails":false,"layout":"month_view"});
    })();
  }, []);

  useEffect(() => {
    let timeout;
    const currentFullWord = words[currentWord];

    if (isTyping) {
      if (displayText.length < currentFullWord.length) {
        timeout = setTimeout(() => {
          setDisplayText(currentFullWord.slice(0, displayText.length + 1));
        }, 100);
      } else {
        timeout = setTimeout(() => {
          setIsTyping(false);
        }, 1500);
      }
    } else {
      if (displayText.length > 0) {
        timeout = setTimeout(() => {
          setDisplayText(displayText.slice(0, -1));
        }, 50);
      } else {
        setCurrentWord((prev) => (prev + 1) % words.length);
        setIsTyping(true);
      }
    }

    return () => clearTimeout(timeout);
  }, [displayText, isTyping, currentWord, words]);

  // Intersection observer for bouncing ball animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !showBallAnimation) {
            setShowBallAnimation(true);
          }
        });
      },
      { threshold: 0.3 }
    );

    const howItWorksSection = document.querySelector('.how-it-works-section');
    if (howItWorksSection) {
      observer.observe(howItWorksSection);
    }

    return () => {
      if (howItWorksSection) {
        observer.unobserve(howItWorksSection);
      }
    };
  }, [showBallAnimation]);

  // Image cycling effect - switch between card images and phone demo every 3.5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      
      // After transition starts, switch image set
      setTimeout(() => {
        setCurrentImageSet(prev => prev === 0 ? 1 : 0);
        setIsTransitioning(false);
      }, 300); // Half of transition duration
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  // Parabolic ball animation using mathematical functions
  useEffect(() => {
    if (!showBallAnimation) return;

    let animationId;
    const startTime = Date.now();
    const duration = 3000; // 3 seconds total - maintain fast speed throughout
    
    // Card positions (as percentages of container width)
    const cardPositions = [16.66, 50, 83.33];
    const containerHeight = 300; // Approximate height of the section
    
    // Parabolic function: y = ax² + bx + c
    const calculateParabola = (x, startX, endX, startY, endY, peakY) => {
      // Normalize x to range [0, 1]
      const t = (x - startX) / (endX - startX);
      
      // Quadratic Bezier curve for parabolic motion
      // y = (1-t)²*startY + 2*(1-t)*t*peakY + t²*endY
      return Math.pow(1 - t, 2) * startY + 2 * (1 - t) * t * peakY + Math.pow(t, 2) * endY;
    };

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      let x, y;
      
      if (progress < 0.25) {
        // Arc to first card (0% to 25% of animation) - maintain fast timing
        const segmentProgress = progress / 0.25;
        const startX = -5;
        const endX = cardPositions[0];
        x = startX + (endX - startX) * segmentProgress;
        y = calculateParabola(x, startX, endX, 25, 35, -10); // Start even higher, hit top of card, extreme curve
        
        // Trigger card fall when ball hits
        if (segmentProgress > 0.8 && !cardBounces.card1) {
          setCardBounces(prev => ({ ...prev, card1: true }));
          setTimeout(() => setCardBounces(prev => ({ ...prev, card1: false })), 300);
        }
      } else if (progress < 0.5) {
        // Arc to second card (25% to 50% of animation) - maintain fast timing
        const segmentProgress = (progress - 0.25) / 0.25;
        const startX = cardPositions[0];
        const endX = cardPositions[1];
        x = startX + (endX - startX) * segmentProgress;
        y = calculateParabola(x, startX, endX, 35, 35, -15); // Even more extreme curve
        
        // Trigger card fall when ball hits
        if (segmentProgress > 0.8 && !cardBounces.card2) {
          setCardBounces(prev => ({ ...prev, card2: true }));
          setTimeout(() => setCardBounces(prev => ({ ...prev, card2: false })), 300);
        }
      } else if (progress < 0.75) {
        // Arc to third card (50% to 75% of animation) - maintain fast timing
        const segmentProgress = (progress - 0.5) / 0.25;
        const startX = cardPositions[1];
        const endX = cardPositions[2];
        x = startX + (endX - startX) * segmentProgress;
        y = calculateParabola(x, startX, endX, 35, 35, -20); // Most extreme curve
        
        // Trigger card fall when ball hits
        if (segmentProgress > 0.8 && !cardBounces.card3) {
          setCardBounces(prev => ({ ...prev, card3: true }));
          setTimeout(() => setCardBounces(prev => ({ ...prev, card3: false })), 300);
        }
      } else {
        // Final arc off screen (75% to 100% of animation) - fast exit
        const segmentProgress = (progress - 0.75) / 0.25; // 25% of time for fast arc off screen
        const startX = cardPositions[2];
        const endX = 120; // Further off screen
        x = startX + (endX - startX) * segmentProgress;
        
        // Continue the parabolic arc trajectory after bouncing off third card - fast exit
        y = calculateParabola(x, startX, endX, 35, 120, -10); // Bounce up to -10%, then fall to 120% (way off screen)
      }
      
      setBallPosition({ x, y });
      
      if (progress < 1) {
        animationId = requestAnimationFrame(animate);
      }
      // Animation completes once - no restart
    };
    
    animate();
    
    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [showBallAnimation]);

  return (
    <div className="home-page">
      <Navigation />
      <div className="hero-section">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h1>The World's Easiest Loyalty Program</h1>
              <p className="subtitle">
                No <span className="cycling-word">{displayText}</span>
              </p>
              <p className="hero-description">
                Get a customer loyalty program with no friction, no stamp cards, and no hassle.
              </p>
              <div className="cta-buttons">
                <Link to="/businesssignup" className="btn btn-primary">Get Started</Link>
                <button onClick={handleLearnMoreClick} className="btn btn-secondary learn-more-btn">
                  Learn more
                  <svg className="arrow-down" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 16L6 10H18L12 16Z" fill="currentColor"/>
                  </svg>
                </button>
              </div>
              <div className="customer-signin-wrapper">
                <Link to="/customerlogin" className="btn btn-customer-signin">
                  Already a loyalty member? Sign in to view your points
                </Link>
              </div>
            </div>
            <div className="hero-images">
              <div className="image-slider-container">
                <div className={`image-set ${currentImageSet === 0 ? 'active' : ''} ${isTransitioning && currentImageSet === 1 ? 'slide-out' : ''}`}>
                  <div className="images-container">
                    <div className="circular-image-frame">
                      <img src={justTapNewImage} alt="Just Tap and Card" className="circular-image" />
                    </div>
                  </div>
                </div>
                <div className={`image-set ${currentImageSet === 1 ? 'active' : ''} ${isTransitioning && currentImageSet === 0 ? 'slide-out' : ''}`}>
                  <div className="phone-demo-container">
                    <img src={phoneDemoImage} alt="Phone Demo" className="phone-demo-image" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* How it works section */}
      <div id="how-it-works" className="how-it-works-section">
        <div className="container">
          <h2>How it works</h2>
          <div className="cards-grid">
            <div className={`how-it-works-card ${cardBounces.card1 ? 'card-bounce' : ''}`}>
              <div className="card-number">1</div>
              <p>Customers tap your pod at checkout. On their first tap, they simply enter an email.</p>
            </div>
            <div className={`how-it-works-card ${cardBounces.card2 ? 'card-bounce' : ''}`}>
              <div className="card-number">2</div>
              <p>After that, every visit is just a quick tap to collect points.</p>
            </div>
            <div className={`how-it-works-card ${cardBounces.card3 ? 'card-bounce' : ''}`}>
              <div className="card-number">3</div>
              <p>Once they've earned enough points (you set the threshold), customers redeem rewards by showing you a secure code on their phone.</p>
            </div>
          </div>
          {showBallAnimation && (
            <div 
              className="bouncing-ball-js"
              style={{
                left: `${ballPosition.x}%`,
                top: `${ballPosition.y}%`,
              }}
            ></div>
          )}
        </div>
      </div>

      {/* Why Bounce Rewards section */}
      <div id="features" className="why-bounce-section">
        <div className="container">
          <h2>Why Bounce Rewards?</h2>
          <p className="why-subtitle">Why it's better than Punch Cards or your POS System</p>
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z" fill="currentColor"/>
                </svg>
              </div>
              <h3>NFC Tags = No friction</h3>
              <p>Unlike punch cards that get lost or apps that require downloads, NFC works instantly. Customers just tap their phone. It's as fast and seamless as paying with Apple pay.</p>
            </div>
            
            <div className="benefit-card">
              <div className="benefit-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L3.09 8.26L12 14L20.91 8.26L12 2ZM21 16V10L12 16L3 10V16L12 22L21 16Z" fill="currentColor"/>
                </svg>
              </div>
              <h3>Custom Branding</h3>
              <p>The program looks and feels like your own: your logo, your colors, your business identity. Customers experience it as your loyalty program, not a generic app.</p>
            </div>
            
            <div className="benefit-card">
              <div className="benefit-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12S6.48 22 12 22 22 17.52 22 12 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="currentColor"/>
                </svg>
              </div>
              <h3>Simple Setup</h3>
              <p>No complicated tech or training. Setup takes less than five minutes: upload your logo, choose point values and rewards, and we'll install the NFC pod for you.</p>
            </div>
            
            <div className="benefit-card">
              <div className="benefit-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16 4C18.2 4 20 5.8 20 8C20 10.2 18.2 12 16 12C13.8 12 12 10.2 12 8C12 5.8 13.8 4 16 4ZM8 12C10.2 12 12 13.8 12 16C12 18.2 10.2 20 8 20C5.8 20 4 18.2 4 16C4 13.8 5.8 12 8 12ZM16 14C18.2 14 20 15.8 20 18C20 20.2 18.2 22 16 22C13.8 22 12 20.2 12 18C12 15.8 13.8 14 16 14Z" fill="currentColor"/>
                </svg>
              </div>
              <h3>Customer Insights & Marketing Tools</h3>
              <p>Each sign-up captures customer emails (and optionally phone numbers), giving you a valuable marketing list. Access built-in tools to create email campaigns, special offers, and targeted promotions.</p>
            </div>
            
            <div className="benefit-card">
              <div className="benefit-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 8H17V6C17 3.24 14.76 1 12 1S7 3.24 7 6V8H6C4.9 8 4 8.9 4 10V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V10C20 8.9 19.1 8 18 8ZM12 17C10.9 17 10 16.1 10 15S10.9 13 12 13 14 13.9 14 15 13.1 17 12 17ZM15.1 8H8.9V6C8.9 4.29 10.29 2.9 12 2.9S15.1 4.29 15.1 6V8Z" fill="currentColor"/>
                </svg>
              </div>
              <h3>Secure & Reliable</h3>
              <p>Our NFC tags use NTAG 424 DNA technology, ensuring encrypted, tamper-proof tracking. Ongoing support is included and we'll make sure everything runs smoothly for years to come.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Comparison Section */}
      <ComparisonSection />

      {/* Final Call to Action Section */}
      <div className="final-cta-section">
        <div className="container">
          <div className="final-cta-content">
            <h2>Ready to grow your business with  easy loyalty?</h2>
            <p>Get started right now, or book a call to learn more.</p>
            <div className="final-cta-buttons">
              <Link to="/businesssignup" className="btn btn-primary">Sign up</Link>
              <button 
                data-cal-namespace="problem-discovery"
                data-cal-link="jakobstrozberg/problem-discovery"
                data-cal-config='{"layout":"month_view","theme":"light"}'
                className="btn btn-secondary"
              >
                Book a Demo
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="home-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h3>Bounce Loyalty</h3>
              <p>The world's easiest loyalty program.</p>
            </div>
            <div className="footer-section">
              <h4>Product</h4>
              <ul>
                <li><a href="#features">Features</a></li>
                <li><a href="#how-it-works">How it Works</a></li>
                <li><Link to="/pricing">Pricing</Link></li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>Company</h4>
              <ul>
                <li><a href="mailto:jakob.solstrozberg@torontomu.ca">Contact</a></li>
                <li><Link to="/about">About Us</Link></li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>Get Started</h4>
              <ul>
                <li><Link to="/businesssignup">Sign Up</Link></li>
                <li><Link to="/businesssignin">Sign In</Link></li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <div className="proudly-canadian">
              <img src={flagImage} alt="Canadian Flag" className="canadian-flag" />
              <span>Proudly Canadian</span>
            </div>
            <p>&copy; 2025 Bounce Loyalty. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
